import { handleAppError } from "@main/infra/handle-app-error";
import { isDocumentPipSupported, openPipWindow, type PipController } from "@main/infra/pip-window";
import {
  type Accessor,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  type ParentProps,
  useContext,
} from "solid-js";

/**
 * §5.2 / Phase 2: Document PiP Window controller を Provider scope で管理する。
 *
 * Provider scope は AppLayout root (= app 全域)。 配下の session-page /
 * settings-panel / session-detail-page から `usePipController()` で利用する。
 *
 * 設計の意図 (PoC B + state machine と整合):
 * - 「いつ controller を auto close すべきか」 「いつ open 状態を保つべきか」 は **caller が
 *   Accessor で inject する**。 Provider 自身は session-store のような特定 state 機構を直接
 *   import しない。
 *   - `shouldAutoClose`: true になったとき controller を強制 close (= idle / completed 等)
 *   - `shouldKeepOpen`: reopen() 中の各遷移点で false なら新 controller を畳む (= 走行中限定)
 * - PoC B 元の意図 (state-driven Provider scope) は URL 設計最適化で Layout scope に移譲済。
 *   ここでは更に「state に応じた controller の lifecycle 自動制御」 を Provider 内部に閉じる
 * - session-page の closure 変数 (pipController / disposePipControls / pipTransitionInFlight /
 *   reopenInFlight) を本 Provider に移譲し、 race 制御 (close → reopen 直列化) も Provider
 *   内部に隠蔽する
 * - controller.close() の呼び出しを Provider 1 箇所に集約 (= §5.2 「dispose 公開禁止」 準拠)
 *
 * 残る課題 (frame-loop の Provider 化は別 phase):
 * - frame-loop の rAF source 切替は session-page の責務として残置。 onPipClosed / reopen の
 *   afterReopen callback で session-page に通知する経路で連携する
 */

export type PipControllerContextValue = {
  /** 現在の active controller (signal)。 'idle' / 'completed' state では null。 */
  controller: Accessor<PipController | null>;
  /**
   * sync click handler から呼ぶ (= user activation の transient consumer)。 既存 controller が
   * あれば no-op (冪等)。 無ければ `openPipWindow()` を sync で起動し、 Promise を内部に保留する。
   * Document PiP 未サポート環境では何もしない。
   */
  openIfNeeded: () => void;
  /**
   * 保留中の openPipWindow Promise を await し、 controller を確定させる + closed event の
   * subscribe を内部で行う。 SessionPageActive.onMount で呼ぶ。
   * controller が null (= PiP 未サポート / open 失敗) なら null を return。
   */
  acquire: () => Promise<PipController | null>;
  /**
   * PiP 手動 close 検知時に呼ばれる callback を登録する (= Set ベースの複数 listener 対応)。
   * 戻り値は unsubscribe 関数。 onCleanup で 必ず呼ぶこと。
   * callback は Promise を返してよい (Provider が await して frame-loop restart の race を排除する)。
   * 利用側: session-page の handlePipClosed (= 自動ミュート + frame-loop 切替) と
   * PipControls の Solid root を dispose する callback。
   */
  onPipClosed: (callback: () => Promise<void> | void) => () => void;
  /**
   * 自動ミュート解除時の PiP 再 open。 close → reopen の race を Provider 内部で直列化する。
   * afterReopen は新規 PiP Window が確保された直後に呼ばれる (frame-loop の rAF source 切替用)。
   * Promise を返してよい (Provider が await することで frame-loop restart 完了まで transition を継続)。
   */
  reopen: (afterReopen: (window: Window | null) => Promise<void> | void) => Promise<void>;
  /** 明示的な close (idempotent)。 通常は sessionLifecycle が 'completed' / 'idle' で auto。 */
  close: () => void;
};

const PipControllerContext = createContext<PipControllerContextValue | null>(null);

type PipControllerProviderProps = ParentProps & {
  /**
   * §5.2 decouple: caller が「いつ controller を auto close すべきか」 を Accessor で inject。
   * createEffect が true 検知時に controller を強制 close する。 デフォルト = 常に false (= 自動 close しない)。
   * 例: app では `() => sessionLifecycle() === 'idle' || sessionLifecycle() === 'completed'`。
   */
  shouldAutoClose?: Accessor<boolean>;
  /**
   * §5.2 decouple: reopen() 中の各遷移点で「PiP を開いた状態を保つべきか」 を Accessor で inject。
   * false なら新 controller を即 close して transition を畳む (= reopen 中に session が終了したケース)。
   * デフォルト = 常に true (= caller が呼んだ reopen は無条件に完走)。
   * 例: app では `() => sessionLifecycle() === 'running'`。
   */
  shouldKeepOpen?: Accessor<boolean>;
};

export function PipControllerProvider(props: PipControllerProviderProps) {
  const [controller, setController] = createSignal<PipController | null>(null);
  let pendingPromise: Promise<PipController | null> | null = null;
  let transitionInFlight: Promise<void> | null = null;
  let reopenInFlight = false;
  // §5.2: PiP 手動 close を観測したい subscriber が複数存在する (session-page の handlePipClosed +
  // dispose callback)。 Set で複数 listener を保持し onPipClosed が
  // unsubscribe 関数を返すことで自動掃除する。 listener が Promise を返したら順次 await して
  // frame-loop restart などの後続処理を transition 内に取り込む (= reopen との race を排除)。
  const closedListeners = new Set<() => Promise<void> | void>();

  const closeCurrent = () => {
    const current = controller();
    if (current) current.close();
    setController(null);
    if (pendingPromise) {
      // §5.2 race fix: openPipWindow が close 後に解決した場合、 誰も握らない PiP Window が orphan
      // 化する。 自前 .then で resolve 値を捕まえて畳む。 openIfNeeded() は reject 経路を null に
      // 置き換えてから pendingPromise に入れているため、 ここでは resolve 経路 (= non-null) のみ care。
      const stillPending = pendingPromise;
      pendingPromise = null;
      stillPending.then((c) => c?.close()).catch(() => {});
    }
  };

  // §UC-08 / Phase 2: caller が指定した条件で auto close。 例えば session app では
  // 'idle' / 'completed' に戻ったときに自動 close する。 starting / running は PiP を保持する。
  createEffect(() => {
    if (props.shouldAutoClose?.()) {
      closeCurrent();
    }
  });

  onCleanup(closeCurrent);

  const openIfNeeded = () => {
    if (!isDocumentPipSupported()) return;
    if (controller() || pendingPromise) return;
    pendingPromise = openPipWindow().catch((err: unknown) => {
      handleAppError(err);
      return null;
    });
  };

  const subscribeClosedEvents = (next: PipController) => {
    next.closed
      .then(() => {
        if (controller() !== next) return;
        setController(null);
        // listener (= session-page の handlePipClosed 経由 frame-loop restart) を transition
        // として登録する。 これで後続の reopen が `await transitionInFlight` で listener 完了を
        // 待てる (= frame-loop の二重起動 race を構造的に排除)。
        const closeTransition = (async () => {
          for (const listener of closedListeners) {
            await listener();
          }
        })();
        const wrapped = closeTransition.finally(() => {
          if (transitionInFlight === wrapped) transitionInFlight = null;
        });
        transitionInFlight = wrapped;
      })
      .catch(handleAppError);
  };

  const acquire = async (): Promise<PipController | null> => {
    const existing = controller();
    if (existing) return existing;
    if (!pendingPromise) return null;
    const next = await pendingPromise;
    pendingPromise = null;
    if (!next) return null;
    setController(next);
    subscribeClosedEvents(next);
    return next;
  };

  const onPipClosed = (callback: () => Promise<void> | void): (() => void) => {
    closedListeners.add(callback);
    return () => {
      closedListeners.delete(callback);
    };
  };

  // §5.2 decouple: shouldKeepOpen 未指定なら無条件に reopen 完走 (= generic Provider として機能)。
  const keepOpen = (): boolean => props.shouldKeepOpen?.() ?? true;

  const reopen = async (afterReopen: (window: Window | null) => Promise<void> | void): Promise<void> => {
    // §UX: PiP の close → reopen を高速反復したときの race を Provider 内部で直列化する。
    // reopenInFlight は二重 unmute 入力 (TOCTOU race) を await 前に同期で予約することで防ぐ。
    if (reopenInFlight) return;
    reopenInFlight = true;
    if (transitionInFlight) await transitionInFlight.catch(() => {});
    if (!keepOpen() || controller()) {
      reopenInFlight = false;
      return;
    }
    const transition = (async () => {
      try {
        const next = await openPipWindow();
        if (!keepOpen()) {
          next.close();
          return;
        }
        setController(next);
        subscribeClosedEvents(next);
        // afterReopen は frame-loop の rAF source 切替を担う。 Promise を返してきたら await して
        // transition 内に取り込む (= 後続の close → reopen race を構造的に排除)。
        await afterReopen(next.pipWindow);
      } catch (err) {
        handleAppError(err);
      }
    })();
    // Promise.prototype.finally() は新しい Promise を返すため、 同一参照比較できない。
    // wrapped をローカル const に確保して transitionInFlight === wrapped で比較する。
    const wrapped = transition.finally(() => {
      reopenInFlight = false;
      if (transitionInFlight === wrapped) transitionInFlight = null;
    });
    transitionInFlight = wrapped;
    await wrapped;
  };

  const close = () => {
    closeCurrent();
  };

  return (
    <PipControllerContext.Provider value={{ controller, openIfNeeded, acquire, onPipClosed, reopen, close }}>
      {props.children}
    </PipControllerContext.Provider>
  );
}

export function usePipController(): PipControllerContextValue {
  const value = useContext(PipControllerContext);
  if (!value) throw new Error("usePipController must be called inside <PipControllerProvider>");
  return value;
}
